import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, ShieldCheck, Clock } from "lucide-react";
import { COMPANY } from "@/lib/siteData";

const LINES = ["ALL YOUR PLUMBING,", "HYDRONICS & DRAIN", "UNDER CONTROL."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.13 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden">
      {/* soft radial light */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#007AFF]/10 blur-[120px]" />
      <div className="pointer-events-none absolute top-20 right-0 h-[420px] w-[420px] rounded-full bg-[#FF3B30]/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-xs font-semibold tracking-wide text-neutral-600 mb-7"
          >
            <span className="h-2 w-2 rounded-full bg-[#007AFF] animate-pulse" />
            Serving the entire GTA • 24/7 Emergency Service
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-[5.4rem] text-[#007AFF]">
            {LINES.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span custom={i} variants={lineVariants} initial="hidden" animate="visible" className="block">
                  {i === 2 ? <span className="text-[#FF3B30]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-quote-btn"
              onClick={() => go("#quote")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#FFB300] text-neutral-900 font-bold px-7 py-3.5 hover:bg-[#ffc233] transition-colors duration-300"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <a
              data-testid="hero-call-btn"
              href={COMPANY.phoneHref}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-black/15 bg-white font-semibold px-7 py-3.5 hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors duration-300"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-neutral-600"
          >
            A licensed, family owned and operated plumbing company you can actually reach. From dripping faucets to full hydronic heating systems — precise work, upfront pricing, and one trusted team from start to finish.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-neutral-500"
          >
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#007AFF]" /> Licensed & Insured</span>
            <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-[#FF3B30]" /> Same-day availability</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] rounded-[2rem] overflow-hidden ring-1 ring-black/10 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)]"
          >
            <motion.img
              style={{ y: imgY }}
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_aqua-home-plumb/artifacts/yx539lmk_IMG_1104.jpeg"
              alt="Aqua Control plumber at work"
              className="absolute inset-0 h-[125%] w-full object-cover"
            />
          </motion.div>

          {/* stat card below the image */}
          <div className="mt-4 rounded-2xl bg-white ring-1 ring-black/10 shadow-sm p-4 flex items-center gap-4">
            <div className="text-3xl font-display font-extrabold brand-gradient-text">15+</div>
            <div className="text-sm text-neutral-700 leading-tight">Years of hands-on<br />plumbing &amp; hydronics.</div>
          </div>
        </div>
      </div>
    </section>
  );
};
